import { useMemo, useState } from 'react'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { TransactionRow } from '@/components/TransactionRow'
import { EmptyState, Skeleton } from '@/components/States'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import styles from '@/features/transfers/SendAbroadScreen.module.css'

type Filter = 'all' | 'in' | 'out'

const FILTERS: ReadonlyArray<{ readonly id: Filter; readonly label: string }> = [
  { id: 'all', label: 'Everything' },
  { id: 'in', label: 'Money in' },
  { id: 'out', label: 'Money out' },
]

const monthFormatter = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' })

export function TransactionsScreen() {
  const repository = useRepository()
  const transactions = useQuery(() => repository.listTransactions(), [repository])
  const [filter, setFilter] = useState<Filter>('all')
  const [term, setTerm] = useState('')

  const grouped = useMemo(() => {
    const rows = (transactions.data ?? [])
      .filter((row) => (filter === 'all' ? true : row.direction === filter))
      .filter((row) =>
        term.trim() === ''
          ? true
          : `${row.counterparty} ${row.category}`.toLowerCase().includes(term.toLowerCase()),
      )
    const buckets = new Map<string, typeof rows>()
    for (const row of rows) {
      const key = monthFormatter.format(new Date(row.occurredAt))
      buckets.set(key, [...(buckets.get(key) ?? []), row])
    }
    return [...buckets.entries()]
  }, [transactions.data, filter, term])

  return (
    <>
      <PageHeader
        title="Transactions"
        subtitle="Everything that moved, with what each one earned back."
        actions={
          <Button variant="ghost" size="md" iconStart="download">
            Export
          </Button>
        }
      />

      <Card tone="light">
        <Stack direction="row" gap={2} wrap align="center">
          {FILTERS.map((item) => (
            <Button
              key={item.id}
              variant={filter === item.id ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </Button>
          ))}
          <div className={styles.amountRow} style={{ flex: 1, minWidth: '12rem' }}>
            <Icon name="search" size={18} />
            <input
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Search a merchant or person"
              aria-label="Search transactions"
              style={{
                flex: 1,
                border: 0,
                background: 'none',
                outline: 'none',
                fontSize: 'var(--pb-text-xs)',
              }}
            />
          </div>
        </Stack>

        <Stack gap={5} className="pb-mt-5">
          {transactions.status === 'loading' && <Skeleton height={320} />}
          {transactions.status === 'ready' && grouped.length === 0 && (
            <EmptyState icon="search_off" title="Nothing matches" body="Try a different word or filter." />
          )}
          {grouped.map(([month, rows]) => (
            <Stack key={month} gap={1}>
              <Stack direction="row" justify="between" align="center">
                <Text variant="eyebrow" weight="semibold" tone="secondary">
                  {month}
                </Text>
                <Pill tone="neutral">{rows.length}</Pill>
              </Stack>
              <div>
                {rows.map((row) => (
                  <TransactionRow key={row.id} transaction={row} />
                ))}
              </div>
            </Stack>
          ))}
        </Stack>
      </Card>
    </>
  )
}
